import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingIcon from "@mui/icons-material/Pending";
import DocumentList, {
  Folder,
  Document,
  NavigationState,
  DocumentListRefreshResult,
} from "./document-list";
import {
  folderPathsEqual,
  resolveBrowseUrlKeys,
  toBrowseUrlKeys,
} from "../../utils/documentSearchNavigation";
import type { NavigateFolderPathOptions } from "../../hooks/useDocumentSearchFolderNavigation";

export interface GroupedFolder {
  tailNumber: string;
  folders: Folder[];
  totalDocuments: number;
}

interface GroupedDocumentListProps {
  groupedData: GroupedFolder[];
  onViewDocument: (doc: Document) => void;
  onDownloadDocument: (doc: Document) => void;
  onDeleteDocument: (doc: Document) => void;
  lastSearchRequest: any;
  onRefresh?: () => Promise<DocumentListRefreshResult | null | void>;
  quickUploadHistoryEpoch?: number;
  /** Active folder path from the URL (browser history). */
  folderPath?: string[];
  /** Active aircraft group from the URL. */
  folderGroup?: string | null;
  onNavigateFolderPath?: (path: string[], options?: NavigateFolderPathOptions) => void;
}

const GroupedDocumentList: React.FC<GroupedDocumentListProps> = ({
  groupedData,
  onViewDocument,
  onDownloadDocument,
  onDeleteDocument,
  lastSearchRequest,
  onRefresh,
  quickUploadHistoryEpoch = 0,
  folderPath = [],
  folderGroup = null,
  onNavigateFolderPath,
}) => {
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
  const [navigationStates, setNavigationStates] = useState<Record<string, NavigationState>>({});

  // Reset accordion states when a new grouped result set arrives.
  React.useEffect(() => {
    setExpandedPanels(new Set());
    setNavigationStates({});
  }, [groupedData]);

  // Keep the active group's navigation in sync with the URL (Back/Forward).
  // URL segments are codes/ids; per-group navigationState keeps display names.
  React.useEffect(() => {
    if (!folderGroup) {
      return;
    }

    const group = groupedData.find((item) => item.tailNumber === folderGroup);
    if (!group) {
      return;
    }

    const resolved = resolveBrowseUrlKeys(folderPath, group.folders);
    if (
      group.folders.length > 0 &&
      !resolved.ambiguous &&
      !folderPathsEqual(resolved.urlKeys, folderPath) &&
      onNavigateFolderPath
    ) {
      onNavigateFolderPath(resolved.urlKeys, {
        group: folderGroup,
        replace: true,
      });
      return;
    }

    const folder =
      resolved.namePath.length > 0
        ? (() => {
            let currentFolders = group.folders;
            let currentFolder: Folder | null = null;
            for (const name of resolved.namePath) {
              currentFolder = currentFolders.find((f) => f.name === name) || null;
              if (!currentFolder) {
                return null;
              }
              currentFolders = currentFolder.children;
            }
            return currentFolder;
          })()
        : null;

    setNavigationStates((prev) => {
      const previous = prev[folderGroup];
      if (
        previous &&
        folderPathsEqual(previous.currentPath, resolved.namePath) &&
        previous.currentFolder === folder
      ) {
        return prev;
      }

      return {
        ...prev,
        [folderGroup]: {
          currentPath: resolved.namePath,
          currentFolder: folder,
          showingFiles: folder ? folder.documents.length > 0 : false,
        },
      };
    });

    setExpandedPanels((prev) => {
      if (prev.has(folderGroup)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(folderGroup);
      return next;
    });
  }, [folderGroup, folderPath, groupedData, onNavigateFolderPath]);

  const handleAccordionChange = (tailNumber: string) => {
    setExpandedPanels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tailNumber)) {
        newSet.delete(tailNumber);
      } else {
        newSet.add(tailNumber);
      }
      return newSet;
    });
  };

  // Calculate status counts for a group
  const getStatusCounts = (folders: Folder[]) => {
    let completedCount = 0;
    let inProgressCount = 0;
    let failedCount = 0;

    const countInFolder = (folder: Folder) => {
      folder.documents.forEach(doc => {
        if (doc.status === "Approved" || doc.status === "Draft") {
          completedCount++;
        } else if (doc.status === "UnderReview") {
          inProgressCount++;
        } else if (doc.status === "FailedReview") {
          failedCount++;
        }
      });
      folder.children.forEach(child => countInFolder(child));
    };

    folders.forEach(folder => countInFolder(folder));
    return { completedCount, inProgressCount, failedCount };
  };

  // Get navigation state for a specific group
  const getNavigationState = (tailNumber: string): NavigationState => {
    return navigationStates[tailNumber] || {
      currentPath: [],
      currentFolder: null,
      showingFiles: false,
    };
  };

  // Update navigation state for a specific group
  const updateNavigationState = (tailNumber: string, newState: NavigationState) => {
    setNavigationStates(prev => ({
      ...prev,
      [tailNumber]: newState,
    }));
  };

  // Find folder by path in a folder tree
  const findFolderByPath = (path: string[], folders: Folder[]): Folder | null => {
    if (path.length === 0) return null;
    let currentFolders = folders;
    let currentFolder: Folder | null = null;
    for (const name of path) {
      currentFolder = currentFolders.find((f) => f.name === name) || null;
      if (!currentFolder) return null;
      currentFolders = currentFolder.children;
    }
    return currentFolder;
  };

  // Navigation handlers for each group
  const createNavigationHandlers = (tailNumber: string, folders: Folder[]) => {
    const handleToggleFolder = (index: number) => {
      // Not used in current implementation but required by DocumentList
    };

    const handleNavigateToFolder = (folderName: string) => {
      const navState = getNavigationState(tailNumber);
      const newPath = [...navState.currentPath, folderName];
      const folder = findFolderByPath(newPath, folders);

      if (!folder) {
        return;
      }

      const nextState: NavigationState = {
        currentPath: newPath,
        currentFolder: folder,
        showingFiles: folder.documents.length > 0,
      };
      updateNavigationState(tailNumber, nextState);

      if (onNavigateFolderPath) {
        onNavigateFolderPath(toBrowseUrlKeys(newPath, folders), {
          group: tailNumber,
        });
      }
    };

    const handleNavigateBack = (
      targetPath: string[],
      options?: { replace?: boolean }
    ) => {
      const folder = targetPath.length > 0 ? findFolderByPath(targetPath, folders) : null;
      updateNavigationState(tailNumber, {
        currentPath: targetPath,
        currentFolder: folder,
        showingFiles: folder ? folder.documents.length > 0 : false,
      });

      if (onNavigateFolderPath) {
        onNavigateFolderPath(toBrowseUrlKeys(targetPath, folders), {
          group: targetPath.length === 0 ? null : tailNumber,
          replace: options?.replace === true,
        });
      }
    };

    return { handleToggleFolder, handleNavigateToFolder, handleNavigateBack };
  };

  return (
    <Box sx={{ width: "100%" }}>
      {groupedData.map((group) => {
        const { completedCount, inProgressCount, failedCount } = getStatusCounts(group.folders);
        const navState = getNavigationState(group.tailNumber);
        const { handleToggleFolder, handleNavigateToFolder, handleNavigateBack } = 
          createNavigationHandlers(group.tailNumber, group.folders);

        return (
          <Accordion
            key={group.tailNumber}
            expanded={expandedPanels.has(group.tailNumber)}
            onChange={() => handleAccordionChange(group.tailNumber)}
            sx={{
              background: "none",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              mb: 2,
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
                  {group.tailNumber}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {completedCount > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "1.1rem" }} />
                      <Typography sx={{ color: "#4caf50", fontSize: "0.9rem" }}>
                        {completedCount}
                      </Typography>
                    </Box>
                  )}
                  {inProgressCount > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <ErrorOutlineIcon sx={{ color: "#2196f3", fontSize: "1.1rem" }} />
                      <Typography sx={{ color: "#2196f3", fontSize: "0.9rem" }}>
                        {inProgressCount}
                      </Typography>
                    </Box>
                  )}
                  {failedCount > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PendingIcon sx={{ color: "#ff9800", fontSize: "1.1rem" }} />
                      <Typography sx={{ color: "#ff9800", fontSize: "0.9rem" }}>
                        {failedCount}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <DocumentList
                folders={group.folders}
                navigationState={navState}
                onToggleFolder={handleToggleFolder}
                onNavigateToFolder={handleNavigateToFolder}
                onNavigateBack={handleNavigateBack}
                onViewDocument={onViewDocument}
                onDownloadDocument={onDownloadDocument}
                onDeleteDocument={onDeleteDocument}
                lastSearchRequest={lastSearchRequest}
                overrideTailNumber={group.tailNumber}
                onRefresh={onRefresh}
                isGroupedMode={true}
                quickUploadHistoryEpoch={quickUploadHistoryEpoch}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default GroupedDocumentList;
